import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  Field,
  HelperText,
  Label,
  NumberInput,
  TextInput,
} from '../../../../components/UiComponents/Ui';
import {
  asString,
  checkNumber,
  getAllocationRules,
  getServerError,
  redirectFields,
  typeOptions,
  withValueError,
} from '../allocationUtils.js';
import { getError, handleChange } from '../../../helperFunctions';
import MultiSelectDropdown from '../../../../components/MultiSelectDropdown';
import { RiCheckboxMultipleBlankLine, RiCloseLine } from 'react-icons/ri';
import { validateEditSurveyAllocationSchema } from '../surveyDetailsValidators';
import { useGlobalContext } from '../../../../Contexts/GlobalContext.js';
import { submitEditAllocation } from '../../../../Actions/surveyAllocationActions.js';

const toEditForm = (a) => ({
  CPI: { label: 'number', value: String(a?.CPI ?? '') },
  requiredSample: {
    label: 'number',
    value: String(a?.requiredSample ?? ''),
  },
  globalRedirects: Boolean(a?.globalRedirects),
  redirects: { ...(a?.redirects || {}) },
});

const getCompletedCount = (a) =>
  Number(a?.progress?.totalCompletes ?? a?.completes ?? 0) || 0;

const CAP_TO_REMAINING = false;

const sameOption = (x, y) =>
  x?.label === y?.label && String(x?.value ?? '') === String(y?.value ?? '');

const checkEditDirty = (original, current) => {
  if (!original || !current) return false;
  const base = toEditForm(original);
  if (
    !sameOption(base.CPI, current.CPI) ||
    !sameOption(base.requiredSample, current.requiredSample)
  )
    return true;
  if (current.globalRedirects) return false;
  return redirectFields.some(
    (rd) =>
      (base.redirects?.[rd.dbFieldName] || '') !==
      (current.redirects?.[rd.dbFieldName] || ''),
  );
};

const EditAllocation = ({
  data,
  id,
  editAllocationModal,
  setEditAllocationModal,
  editingAllocation,
  setEditingAllocation,
  mutateAllocations,
}) => {
  const { setSnackbar } = useGlobalContext();
  const [editData, setEditData] = useState(toEditForm(editingAllocation));
  const [editErrors, setEditErrors] = useState({});
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const isEditDirty = checkEditDirty(editingAllocation, editData);
  const editRules = editData
    ? getAllocationRules({
        sampleType: editData.requiredSample?.label,
        cpiType: editData.CPI?.label,
        expectedCompletes: data?.expectedMatrix?.expectedCompletes,
        surveyCpi: data?.setup?.surveyCpi,
        completed: getCompletedCount(
          editingAllocation?.sessionDetails?.completed,
        ),
      })
    : null;

  useEffect(() => {
    setEditData(toEditForm(editingAllocation));
  }, [editingAllocation?._id]);
  const handleCloseEditAllocation = () => {
    if (isEditSubmitting) return;
    if (isEditDirty && !window.confirm('Discard your changes?')) return;
    setEditAllocationModal(false);
  };

  const resetEditState = () => {
    setEditingAllocation(null);
    setEditData(null);
    setEditErrors({});
  };

  const handleEditTypeChange = (field, nextType) => {
    if (!nextType || nextType === editData?.[field]?.label) return;
    setEditData((prev) => ({
      ...prev,
      [field]: { ...prev[field], label: nextType, value: '' },
    }));
    setEditErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmitEditAllocation = async (e) => {
    e.preventDefault();
    if (isEditSubmitting || !editData || !isEditDirty) return;

    try {
      const candidate = {
        CPI: editData.CPI,
        requiredSample: editData.requiredSample,
        globalRedirects: editData.globalRedirects,
        ...(editData.globalRedirects
          ? {}
          : { redirects: editData.redirects || {} }),
      };

      const result = validateEditSurveyAllocationSchema.safeParse(candidate);
      let errors = result.success ? {} : formateZodError(result.error.errors);
      errors = withValueError(
        errors,
        'requiredSample',
        checkNumber(
          'Required number',
          editData.requiredSample?.value,
          editRules.sampleRule,
          editRules.samplePct,
        ),
      );
      errors = withValueError(
        errors,
        'CPI',
        checkNumber(
          'CPI',
          editData.CPI?.value,
          editRules.cpiRule,
          editRules.cpiPct,
        ),
      );
      if (Object.keys(errors).length) {
        setEditErrors(errors);
        return;
      }
      setEditErrors({});

      setIsEditSubmitting(true);

      const response = await submitEditAllocation({
        allocationId: editingAllocation._id,
        surveyId: id,
        ...result.data,
      });
      if (response?.error)
        throw new Error(
          getServerError(response, 'Unable to update allocation'),
        );

      await mutateAllocations();
      setSnackbar({
        open: true,
        msg: 'Allocation updated successfully',
        severity: 'success',
      });
      setEditAllocationModal(false);
    } catch (error) {
      setSnackbar({
        open: true,
        msg: error?.message || 'Something went wrong !',
        severity: 'error',
      });
    } finally {
      setIsEditSubmitting(false);
    }
  };
  // console.log('editRules=>', editRules);

  // console.log('editingAllocation=>', editingAllocation);

  return (
    <Dialog
      open={editAllocationModal}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleCloseEditAllocation();
      }}
      TransitionProps={{ onExited: resetEditState }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby={`${id}-edit-title`}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmitEditAllocation,
        sx: { borderRadius: '12px' },
      }}
    >
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h1
              id={`${id}-edit-title`}
              className="text-xl font-[600] text-gray-900"
            >
              Edit Allocation
            </h1>
            <p className="text-sm text-gray-500">
              Update the sample, CPI and redirects for this supplier.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={handleCloseEditAllocation}
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
          >
            <RiCloseLine size={20} />
          </button>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {editData && editRules && (
          <div className="space-y-5">
            {/* supplier (read-only) */}
            <Field label="Supplier">
              <div className="flex items-center gap-2">
                <TextInput
                  disabled
                  value={editingAllocation?.supplierData?.supplierName}
                />
                <Chip intent={editData.globalRedirects ? 'purple' : 'primary'}>
                  {editData.globalRedirects
                    ? 'Global Redirects'
                    : 'Dynamic Redirects'}
                </Chip>
              </div>
              <HelperText error={false}>
                The supplier can't be changed. Add a new allocation instead.
              </HelperText>
            </Field>

            <div className="grid grid-cols-2 items-start gap-4">
              {/* required number */}
              <div className="space-y-1">
                <Label required>Required Number</Label>
                <div className="flex items-start gap-2">
                  <div className="w-full">
                    <NumberInput
                      required
                      placeholder={
                        editRules.samplePct ? 'Sample %' : 'Sample Number'
                      }
                      min={editRules.sampleRule.min}
                      max={editRules.sampleRule.max}
                      step={editRules.sampleRule.integer ? 1 : 'any'}
                      value={editData.requiredSample?.value ?? ''}
                      error={
                        asString(
                          getError(['requiredSample', 'value'], editErrors),
                        ) ||
                        asString(
                          getError(['requiredSample', 'label'], editErrors),
                        )
                      }
                      onChange={(e) =>
                        handleChange(
                          ['requiredSample', 'value'],
                          e.target.value,
                          setEditData,
                          setEditErrors,
                        )
                      }
                    />
                  </div>
                  <MultiSelectDropdown
                    width="120px"
                    options={typeOptions}
                    selected={typeOptions.filter(
                      (o) => o.value === editData.requiredSample?.label,
                    )}
                    setSelected={(v) =>
                      handleEditTypeChange('requiredSample', v?.[0]?.value)
                    }
                  />
                </div>
                {asString(getError(['requiredSample', 'value'], editErrors)) ||
                asString(getError(['requiredSample', 'label'], editErrors)) ? (
                  <HelperText>
                    {asString(
                      getError(['requiredSample', 'value'], editErrors),
                    ) ||
                      asString(
                        getError(['requiredSample', 'label'], editErrors),
                      )}
                  </HelperText>
                ) : editRules.sampleRule.max !== undefined ? (
                  <HelperText error={false}>
                    Between {editRules.sampleRule.min} and{' '}
                    {editRules.sampleRule.max}
                    {editRules.samplePct ? '%' : ''}{' '}
                    {editRules.sampleRule.minNote}
                  </HelperText>
                ) : null}
              </div>

              {/* CPI */}
              <div className="space-y-1">
                <Label required>CPI</Label>
                <div className="flex items-start gap-2">
                  <div className="w-full">
                    <NumberInput
                      required
                      placeholder={editRules.cpiPct ? 'CPI %' : 'Add CPI'}
                      min={editRules.cpiRule.min}
                      max={editRules.cpiRule.max}
                      step="any"
                      value={editData.CPI?.value ?? ''}
                      error={
                        asString(getError(['CPI', 'value'], editErrors)) ||
                        asString(getError(['CPI', 'label'], editErrors))
                      }
                      onChange={(e) =>
                        handleChange(
                          ['CPI', 'value'],
                          e.target.value,
                          setEditData,
                          setEditErrors,
                        )
                      }
                    />
                  </div>
                  <MultiSelectDropdown
                    width="120px"
                    options={typeOptions}
                    selected={typeOptions.filter(
                      (o) => o.value === editData.CPI?.label,
                    )}
                    setSelected={(v) =>
                      handleEditTypeChange('CPI', v?.[0]?.value)
                    }
                  />
                </div>
                {asString(getError(['CPI', 'value'], editErrors)) ||
                asString(getError(['CPI', 'label'], editErrors)) ? (
                  <HelperText>
                    {asString(getError(['CPI', 'value'], editErrors)) ||
                      asString(getError(['CPI', 'label'], editErrors))}
                  </HelperText>
                ) : editRules.cpiRule.max !== undefined ? (
                  <HelperText error={false}>
                    Up to {editRules.cpiRule.max}
                    {editRules.cpiPct ? '%' : ''}
                  </HelperText>
                ) : null}
              </div>
            </div>

            {/* redirects */}
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-900">Redirects</h2>

              {editData.globalRedirects && (
                <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                  This supplier uses global redirects, so they can't be edited
                  here. Change them in the supplier's Redirects tab.
                </p>
              )}

              {!editData.globalRedirects && (
                <div className="space-y-4">
                  {redirectFields?.map((rd) => {
                    const value = editData.redirects?.[rd?.dbFieldName] || '';
                    const fieldError = asString(
                      getError(['redirects', rd?.dbFieldName], editErrors),
                    );
                    return (
                      <div
                        className="flex items-start justify-between"
                        key={rd?.dbFieldName}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`h-[40px] border-l-[1.5px] ${rd.color}`}
                          />
                          <h3 className="text-sm font-medium text-gray-800">
                            {rd?.label}
                          </h3>
                        </div>
                        <div className="w-[60%] space-y-1">
                          <TextInput
                            type="url"
                            required={!editData.globalRedirects}
                            disabled={editData.globalRedirects}
                            placeholder="https://www.example.com/[%rid%]"
                            value={value}
                            error={fieldError}
                            onChange={(e) =>
                              handleChange(
                                ['redirects', rd?.dbFieldName],
                                e.target.value,
                                setEditData,
                                setEditErrors,
                              )
                            }
                            endIcon={
                              <button
                                type="button"
                                aria-label={`Copy ${rd?.label} redirect`}
                                disabled={!value}
                                className="text-gray-500 hover:text-gray-700 disabled:opacity-40"
                                onClick={() => handleCopy(value, rd?.label)}
                              >
                                <RiCheckboxMultipleBlankLine size={18} />
                              </button>
                            }
                          />
                          {!!fieldError && (
                            <HelperText>{fieldError}</HelperText>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="outlined"
          intent="default"
          type="button"
          onClick={handleCloseEditAllocation}
          disabled={isEditSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={!isEditDirty || isEditSubmitting}>
          {isEditSubmitting ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAllocation;
